// Batch / lot selection helpers shared across the POS.
//
// `pickFifoLot` is the single source of truth for "which lot do we default
// to when none was explicitly chosen." It's used both by ProductModal (when
// seeding the batch panel for a fresh add) and by the recall pipeline (when
// an ecom/hold order arrives without a lot on its cannabis lines). Keeping
// one implementation means the lot the cashier sees pre-selected in the
// modal matches the lot stamped on the recalled cart line.

import type { ProductLot } from '@/types'

/**
 * FIFO/FEFO lot pick: the oldest in-stock batch.
 *
 *   1. Only batches with positive on-hand are eligible (negative-quantity
 *      batches are kept in the list as a stock-discrepancy signal but never
 *      auto-selected).
 *   2. Among those, the earliest `expiration_date` wins. Batches without a
 *      date sort last, so they're only chosen when no dated batch has stock.
 *
 * Returns `null` for an empty list. When no batch has positive stock but the
 * list is non-empty, falls back to the first entry so the caller still gets a
 * lot to display rather than nothing.
 */
export function pickFifoLot(list: ProductLot[]): string | null {
  if (list.length === 0) return null
  const positives = list.filter((b) => (b.quantity || 0) > 0)
  if (positives.length === 0) return list[0].lot_number
  const sorted = [...positives].sort((a, b) => {
    const ad = a.expiration_date
    const bd = b.expiration_date
    if (!ad && !bd) return 0
    if (!ad) return 1
    if (!bd) return -1
    return ad.localeCompare(bd)
  })
  return sorted[0].lot_number
}

/**
 * Choose which unconfirmed cart line a scan should confirm.
 *
 * `unconfirmed` is the set of still-to-scan lines for the SCANNED product
 * (the caller has already filtered by product_id + scan_confirmed === false).
 *
 * A recalled order can split one SKU across several lines with different
 * lots/quantities (e.g. LOT-A×5, LOT-B×1). Matching by product_id alone and
 * taking the first line would let a scan of LOT-B land on the LOT-A line —
 * crossing the lot↔quantity pairing. So when the scan carries a lot we prefer
 * the line that ALREADY holds it; the recorded lot then stays attached to the
 * quantity it was allocated to. Only when the scanned lot matches no line (or
 * the scan carried no lot — a plain UPC presence-confirm) do we fall back to
 * the first unconfirmed line.
 *
 * Returns `undefined` when `unconfirmed` is empty (the scanned product isn't a
 * pending line) — callers treat that as "not on this order."
 */
export function pickScanConfirmTarget<T extends { lot_number: string | null }>(
  unconfirmed: T[],
  scannedLot: string | null,
  capsLock?: boolean,
): T | undefined {
  if (scannedLot) {
    // Case-tolerant: CapsLock inverts every scanned letter's case
    // (see findLotIgnoreCase), and a strict === sent those scans to the
    // fallback line below -- crossing exactly the lot↔quantity pairing
    // this function exists to preserve.
    const exact = findLotIgnoreCase(unconfirmed, scannedLot, capsLock)
    if (exact) return exact
  }
  return unconfirmed[0]
}

/** CapsLock's exact transform: every letter's case flips (plain keys
 * come out uppercase, shifted keys lowercase). Applying it again
 * recovers the original string. */
export function invertLetterCase(s: string): string {
  return s.replace(/[a-zA-Z]/g, (ch) =>
    ch === ch.toLowerCase() ? ch.toUpperCase() : ch.toLowerCase(),
  )
}

/**
 * Lot lookup that lets callers ADOPT THE STORED CASING.
 *
 * Keyboard-wedge scanners emit letters through the OS keyboard layer, so
 * CapsLock inverts the case of every scanned letter — lot "ABC123"
 * arrives as "abc123" and a strict === comparison misses.
 *
 * Deliberately NOT fixed by force-uppercasing the scan: the BE nets
 * on-hand by the raw, case-sensitive batch_number, so submitting a
 * casing the receiving journal doesn't have severs the sale from its
 * batch and drifts per-lot stock. Matching against the KNOWN batches and
 * using the matched batch's exact string fixes CapsLock without ever
 * inventing a casing.
 *
 * Match ladder (lots are NOT guaranteed case-insensitively unique):
 * two exact probes — scan-as-received and its CapsLock inversion,
 * ordered by the scan-time CapsLock state when the caller knows it —
 * then case-insensitive, preferring the all-uppercase variant among
 * ties.
 */
export function findLotIgnoreCase<T extends { lot_number: string | null }>(
  list: T[],
  scanned: string,
  capsLock?: boolean,
): T | undefined {
  // Exact probes first. Probe ORDER decides pairs that differ only by
  // casing: CapsLock known-ON means the OS inverted every letter in
  // transit, so the INVERTED form is the barcode's true string and goes
  // first; off/unknown trusts the scan as received. Camera scans never
  // pass the keyboard layer, carry no CapsLock state, and land in the
  // as-received branch.
  const inverted = invertLetterCase(scanned)
  const probes = capsLock === true ? [inverted, scanned] : [scanned, inverted]
  for (const probe of probes) {
    const hit = list.find((l) => l.lot_number === probe)
    if (hit) return hit
  }

  // Case-insensitive fallback for any other mangling. Among multiple
  // candidates prefer the all-uppercase variant — supplier lot labels
  // are overwhelmingly printed uppercase — then first-listed for
  // determinism.
  const needle = scanned.toLowerCase()
  const candidates = list.filter(
    (l) => l.lot_number !== null && l.lot_number.toLowerCase() === needle,
  )
  if (candidates.length === 0) return undefined
  return (
    candidates.find((l) => l.lot_number === l.lot_number!.toUpperCase())
    ?? candidates[0]
  )
}
